/**
 * Menu navigation input: keyboard and gamepad -> `NavAction`.
 *
 * The menu must be fully operable with no mouse, on either device. That is not
 * a checkbox — a menu you can only click is unusable the moment the player has
 * a controller in their hands, which for this game is most of the time.
 *
 * TWO DIFFERENT KINDS OF SIGNAL. A keyboard sends discrete `keydown` events and
 * the OS already does the auto-repeat, so keyboard navigation is a pure
 * function of the event. A gamepad sends LEVEL, sampled by polling: buttons and
 * axes just read as held. Everything a level signal needs — edge detection, a
 * repeat delay, and per-action state so a held stick does not fire 120 times a
 * second — lives in `GamepadNav`, which is deliberately a plain class over an
 * injected clock so the whole thing is testable without a browser.
 *
 * Directions repeat while held; `confirm` and `back` never do. A repeating
 * confirm would fire the focused item again the instant a screen changed under
 * the player's thumb, which is how you accidentally start a match from the
 * pause menu.
 *
 * The menu's own navigation keys are FIXED and not rebindable — arrows and
 * WASD, Enter/Space, Escape. They are not part of `KeyBindings`: a player who
 * rebound their way out of the menu would have no way back in.
 */

export type NavAction = 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back';

const KEY_ACTIONS: Record<string, NavAction> = {
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
  Enter: 'confirm',
  NumpadEnter: 'confirm',
  Space: 'confirm',
  Escape: 'back',
  Backspace: 'back',
};

export const navActionForKey = (code: string): NavAction | null => KEY_ACTIONS[code] ?? null;

/** Directions repeat while held; the other two are strictly edge-triggered. */
export const isDirection = (action: NavAction): boolean =>
  action === 'up' || action === 'down' || action === 'left' || action === 'right';

// ---------------------------------------------------------------------------
// Gamepad
// ---------------------------------------------------------------------------

/**
 * The parts of `Gamepad` we read. Declared structurally so tests can hand in a
 * literal; the real `Gamepad` satisfies it.
 */
export interface PadSnapshot {
  readonly buttons: readonly { readonly pressed: boolean; readonly value: number }[];
  readonly axes: readonly number[];
}

/**
 * Standard-mapping indices. Every mainstream controller reports this layout
 * through `Gamepad.mapping === 'standard'`, and it is the same layout
 * src/input/controller.ts assumes for driving.
 */
const BUTTON = {
  south: 0,
  east: 1,
  dpadUp: 12,
  dpadDown: 13,
  dpadLeft: 14,
  dpadRight: 15,
} as const;

/**
 * Stick deflection at which a direction counts as held, and the smaller value
 * it must fall back below to re-arm. The gap is hysteresis: without it a stick
 * resting exactly on the threshold chatters, and the menu scrolls on its own.
 */
const STICK_ON = 0.6;
const STICK_OFF = 0.35;

/** Hold-to-repeat timing, seconds. Matches the feel of a console menu. */
const REPEAT_DELAY = 0.38;
const REPEAT_INTERVAL = 0.11;

const pressed = (pad: PadSnapshot, index: number): boolean =>
  pad.buttons[index]?.pressed === true;

/**
 * Which directions the pad is currently asking for, from the d-pad OR the left
 * stick. `previous` supplies the hysteresis state for the stick, so a direction
 * already held stays held down to `STICK_OFF`.
 */
export const padDirections = (
  pad: PadSnapshot,
  previous: ReadonlySet<NavAction>
): Set<NavAction> => {
  const out = new Set<NavAction>();
  const x = pad.axes[0] ?? 0;
  const y = pad.axes[1] ?? 0;

  const axisHeld = (value: number, action: NavAction, sign: 1 | -1): void => {
    const v = value * sign;
    const threshold = previous.has(action) ? STICK_OFF : STICK_ON;
    if (Number.isFinite(v) && v >= threshold) out.add(action);
  };

  // Stick Y is negative up, which is the one axis sign everybody gets wrong.
  axisHeld(y, 'up', -1);
  axisHeld(y, 'down', 1);
  axisHeld(x, 'left', -1);
  axisHeld(x, 'right', 1);

  if (pressed(pad, BUTTON.dpadUp)) out.add('up');
  if (pressed(pad, BUTTON.dpadDown)) out.add('down');
  if (pressed(pad, BUTTON.dpadLeft)) out.add('left');
  if (pressed(pad, BUTTON.dpadRight)) out.add('right');

  return out;
};

/** All actions the pad is asserting this instant, buttons included. */
export const padActions = (pad: PadSnapshot, previous: ReadonlySet<NavAction>): Set<NavAction> => {
  const out = padDirections(pad, previous);
  if (pressed(pad, BUTTON.south)) out.add('confirm');
  if (pressed(pad, BUTTON.east)) out.add('back');
  return out;
};

/**
 * Level-to-edge converter with hold-to-repeat. Feed it a snapshot and a
 * monotonic time in seconds; it returns the actions that fired this poll.
 *
 * `poll(null, t)` releases everything, which is what a disconnect must do —
 * otherwise the last held direction stays latched forever and the next
 * reconnect produces no edge at all.
 */
export class GamepadNav {
  private held = new Set<NavAction>();
  private nextRepeat = new Map<NavAction, number>();

  poll(pad: PadSnapshot | null, now: number): NavAction[] {
    const active = pad ? padActions(pad, this.held) : new Set<NavAction>();
    const fired: NavAction[] = [];

    for (const action of active) {
      if (!this.held.has(action)) {
        fired.push(action);
        if (isDirection(action)) this.nextRepeat.set(action, now + REPEAT_DELAY);
      } else if (isDirection(action)) {
        const due = this.nextRepeat.get(action);
        if (due !== undefined && now >= due) {
          fired.push(action);
          this.nextRepeat.set(action, now + REPEAT_INTERVAL);
        }
      }
    }

    for (const action of this.held) {
      if (!active.has(action)) this.nextRepeat.delete(action);
    }

    this.held = active;
    return fired;
  }

  /** Drop all latched state — call when the menu is shown or hidden. */
  reset(): void {
    this.held.clear();
    this.nextRepeat.clear();
  }
}

/**
 * First connected pad, or null. Written defensively because `getGamepads`
 * is absent in Node and in a few locked-down embeddings, and returns a sparse
 * array with holes everywhere else.
 */
export const firstGamepad = (): Gamepad | null => {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
  for (const pad of navigator.getGamepads()) {
    if (pad && pad.connected) return pad;
  }
  return null;
};
